import clsx from "clsx";
import { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight, faFileArrowDown, faSpinner } from "@awesome.me/kit-70c1f0055f/icons/duotone/solid";
import {
  downloadAdjudicationWorksheetXlsx,
  getAdjudicationTask,
  getCaseReviewAdjudicationReadiness,
  listAdjudicationRuns,
  triggerAdjudicationFromCaseReview,
  type AdjudicationCall,
  type CaseReviewAdjudicationReadiness,
  type AdjudicationLenience,
  type AdjudicationRow,
  type AdjudicationRun,
  type AdjudicationTask,
  type WhoIsRight,
} from "@/services/api/case-checking";
import { Button } from "@/components/ui/button/Button";
import { SegmentedPillToggle } from "@/components/ui/segmented-tabs/SegmentedPillToggle";
import {
  CC_ALERT_WARNING_BODY,
  CC_ALERT_WARNING_PANEL,
  CC_ALERT_WARNING_TITLE,
  CC_GHOST_BTN,
  CC_ERROR_TEXT,
  CC_PANEL,
  CC_PANEL_FLUSH,
  CC_SECTION_LABEL,
} from "@/components/case-checking/caseCheckingChrome";
import { CaseCheckingListRow } from "@/components/case-checking/CaseCheckingListRow";
import {
  adjudicatorGradeCallHelper,
  adjudicatorGradeCallLabel,
  CaseListGradeBadge,
  humanReviewDisplayLabel,
  humanReviewHelperText,
  HumanReviewStatusChip,
  normalizeCaseCheckingGrade,
  type HumanReviewRouting,
} from "@/components/case-checking/caseCheckingStatusBadges";
import { adjudicationTaskOutcome, isAgentOnlyAdjudication } from "@/components/case-checking/adjudicationUi";

type LenienceFilter = "all" | AdjudicationLenience;

const VERDICT_LABEL: Record<WhoIsRight, string> = {
  agent: "Agent is right",
  human: "Human reviewer is right",
  partial: "Both have merit",
  unclear: "Unclear",
};

const VERDICT_CLASS: Record<WhoIsRight, string> = {
  agent: "text-emerald-700 dark:text-emerald-400",
  human: "text-sky-700 dark:text-sky-400",
  partial: "text-violet-700 dark:text-violet-400",
  unclear: "text-amber-700 dark:text-amber-400",
};

const LENIENCE_LABEL: Record<AdjudicationLenience, string> = {
  human_more_lenient: "Human more lenient",
  agent_more_lenient: "Agent more lenient",
  equal: "Equal concern",
};

const CALL_LABEL: Record<AdjudicationCall, string> = {
  pass: "Pass",
  fail: "Fail",
  na: "N/A",
  unclear: "Unclear",
};

const CALL_CLASS: Record<AdjudicationCall, string> = {
  pass: "text-emerald-700 dark:text-emerald-400",
  fail: "text-rose-700 dark:text-rose-400",
  na: "text-slate-600 dark:text-slate-400",
  unclear: "text-amber-700 dark:text-amber-400",
};

function LenienceFilterBar({
  value,
  onChange,
  counts,
}: {
  value: LenienceFilter;
  onChange: (next: LenienceFilter) => void;
  counts: Record<AdjudicationLenience, number>;
}) {
  const options = [
    { id: "all" as const, label: "All" },
    {
      id: "human_more_lenient" as const,
      label: `Human more lenient ${counts.human_more_lenient}`,
      disabled: counts.human_more_lenient === 0,
    },
    {
      id: "agent_more_lenient" as const,
      label: `Agent more lenient ${counts.agent_more_lenient}`,
      disabled: counts.agent_more_lenient === 0,
    },
    {
      id: "equal" as const,
      label: `Equal concern ${counts.equal}`,
      disabled: counts.equal === 0,
    },
  ];

  return (
    <div className={clsx(CC_PANEL, "py-3")} data-testid="adjudication-lenience-filter">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className={clsx(CC_SECTION_LABEL, "mb-0.5")}>Filter disagreements</p>
          <p className="text-xs text-slate-500">Group rules by which side read the file more softly.</p>
        </div>
        <SegmentedPillToggle
          options={options}
          value={value}
          onChange={onChange}
          className="max-w-full flex-wrap"
          aria-label="Filter disagreements"
          optionTestIdPrefix="adjudication-lenience"
        />
      </div>
    </div>
  );
}

function DisagreementRow({ row }: { row: AdjudicationRow }) {
  const [open, setOpen] = useState(false);

  const footer = open ? (
    <div className="space-y-3 border-t border-slate-200 px-4 pb-4 dark:border-white/5">
      <div className="grid gap-3 pt-3 text-sm leading-relaxed sm:grid-cols-2">
        <div>
          <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-slate-500">
            Human reviewer
          </p>
          <p className="text-slate-600 dark:text-slate-300">
            {row.human_position || <span className="text-slate-500">Not recorded</span>}
          </p>
        </div>
        <div>
          <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-slate-500">Agent</p>
          <p className="text-slate-600 dark:text-slate-300">
            {row.agent_position || <span className="text-slate-500">Not recorded</span>}
          </p>
        </div>
      </div>
      {row.file_evidence ? (
        <div>
          <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-slate-500">
            File evidence
          </p>
          <p className="text-sm leading-relaxed text-slate-400">{row.file_evidence}</p>
        </div>
      ) : null}
      {row.notes ? (
        <div>
          <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-slate-500">
            Adjudicator
          </p>
          <p className="text-sm leading-relaxed text-slate-700 dark:text-slate-200">{row.notes}</p>
        </div>
      ) : null}
    </div>
  ) : null;

  return (
    <div className="border-t border-slate-200 first:border-t-0 dark:border-white/5">
      <CaseCheckingListRow
        variant="flush"
        onClick={() => setOpen((v) => !v)}
        ariaExpanded={open}
        ariaLabel={`${row.rule_label || "Rule"}, ${open ? "collapse" : "expand"} adjudication details`}
        testId={`adjudication-row-${row.row_id}`}
        footer={footer}
      >
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium leading-snug text-slate-900 dark:text-slate-100">{row.rule_label || "Rule"}</p>
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-slate-500">
            {row.rule_id ? <span>{row.rule_id}</span> : null}
            {row.rule_id ? <span className="text-slate-600">·</span> : null}
            <span>
              Human <span className={CALL_CLASS[row.human_call]}>{CALL_LABEL[row.human_call]}</span> ·
              Agent <span className={CALL_CLASS[row.agent_call]}>{CALL_LABEL[row.agent_call]}</span>
            </span>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2 pt-0.5 text-right text-[11px]">
          <div className="flex flex-col items-end gap-0.5">
            <span className="text-slate-500">{LENIENCE_LABEL[row.lenience]}</span>
            <span className={clsx("font-medium", VERDICT_CLASS[row.who_is_right])}>
              {VERDICT_LABEL[row.who_is_right]}
            </span>
          </div>
          <FontAwesomeIcon
            icon={faChevronRight}
            className={clsx("h-3 w-3 text-slate-500 transition-transform", open && "rotate-90")}
          />
        </div>
      </CaseCheckingListRow>
    </div>
  );
}

function DisagreementsSection({ rows }: { rows: AdjudicationRow[] }) {
  if (rows.length === 0) {
    return (
      <div className={CC_PANEL}>
        <p className="text-sm text-slate-500">No disagreements match this filter.</p>
      </div>
    );
  }

  return (
    <div className={CC_PANEL_FLUSH} data-testid="adjudication-disagreements">
      <div className="px-4 py-3 border-b border-slate-200 dark:border-white/5">
        <p className="text-sm font-medium text-slate-900 dark:text-slate-100">Disagreements</p>
        <p className="mt-0.5 text-xs text-slate-500">
          {rows.length} rule{rows.length === 1 ? "" : "s"} — expand a row for full detail
        </p>
      </div>
      <div>
        {rows.map((row) => (
          <DisagreementRow key={row.row_id} row={row} />
        ))}
      </div>
    </div>
  );
}

function GradeSummary({
  label,
  gradeRaw,
}: {
  label: string;
  gradeRaw: string | null | undefined;
}) {
  const grade = normalizeCaseCheckingGrade(gradeRaw);
  return (
    <div>
      <p className="text-xs text-slate-500 mb-1">{label}</p>
      {grade ? (
        <CaseListGradeBadge grade={grade} className="text-sm" />
      ) : (
        <span className="text-sm text-slate-500">{gradeRaw || "—"}</span>
      )}
    </div>
  );
}

function formatRunTimestamp(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function AdjudicationRunningPanel({
  hadPreviousRun,
  queued,
  slow,
}: {
  hadPreviousRun: boolean;
  queued: boolean;
  slow: boolean;
}) {
  return (
    <div
      className={clsx(
        CC_PANEL,
        "border border-indigo-500/30 bg-indigo-500/5",
      )}
      data-testid="adjudication-running"
    >
      <div className="flex items-start gap-3">
        <FontAwesomeIcon icon={faSpinner} className="mt-0.5 h-4 w-4 shrink-0 text-indigo-400 animate-spin" />
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
            {queued ? "Adjudication queued" : "Adjudication running"}
          </p>
          <p className="mt-1 text-sm text-slate-500 leading-relaxed">
            {queued
              ? "Waiting for the agent to pick this up."
              : "Comparing case review disagreements to the agent assessment, rule by rule. This can take several minutes."}
            {hadPreviousRun ? " Previous results are hidden until this run finishes." : ""}
          </p>
          {slow ? (
            <p className="mt-2 text-xs text-slate-500" data-testid="adjudication-slow">
              This is taking longer than usual. You can leave this page — results will appear here when ready.
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function CaseReviewAdjudicationSetupPanel({
  readiness,
  running,
  triggerError,
  onTrigger,
}: {
  readiness: CaseReviewAdjudicationReadiness | null;
  running: boolean;
  triggerError: string | null;
  onTrigger: () => void;
}) {
  const canTrigger = Boolean(readiness?.can_trigger && !running);
  const statusMessage =
    readiness?.message ??
    (readiness?.can_trigger
      ? `${readiness.disagreement_count} rule disagreement${readiness.disagreement_count === 1 ? "" : "s"} from case review.`
      : null);

  return (
    <div className={CC_PANEL} data-testid="adjudication-setup">
      <p className={clsx(CC_SECTION_LABEL, "mb-1")}>Adjudication vs case review</p>
      <p className="text-sm text-slate-500 leading-relaxed">
        Uses rule overrides from the completed in-app case review where you disagreed with the agent.
        Uploading a separate file review sheet is temporarily disabled.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button
          type="button"
          onClick={onTrigger}
          disabled={!canTrigger}
          data-testid="adjudication-trigger-case-review"
        >
          {running ? "Running…" : "Run adjudication vs case review"}
        </Button>
      </div>

      {readiness?.review_complete && readiness.disagreement_count > 0 ? (
        <div className="mt-4 rounded-lg border border-slate-200 dark:border-white/10 px-4 py-3 text-sm">
          <p className="font-medium text-slate-900 dark:text-slate-100">Case review complete</p>
          <p className="mt-1 text-xs text-slate-500">
            {readiness.reviewer_email ? `Reviewer: ${readiness.reviewer_email}` : "Reviewer not recorded"}
            {` · ${readiness.disagreement_count} disagreement${readiness.disagreement_count === 1 ? "" : "s"}`}
          </p>
        </div>
      ) : null}

      {statusMessage ? (
        <p
          className={clsx("mt-3 text-sm", readiness?.can_trigger ? "text-slate-500" : "text-amber-500")}
          data-testid="adjudication-readiness-message"
        >
          {statusMessage}
        </p>
      ) : null}
      {triggerError ? <p className={clsx("mt-2 text-xs", CC_ERROR_TEXT)}>{triggerError}</p> : null}
    </div>
  );
}

function AdjudicationRunPanel({
  run,
  caseId,
  humanReviewRouting,
  isLatest = true,
}: {
  run: AdjudicationRun;
  caseId: string;
  humanReviewRouting?: HumanReviewRouting | null;
  isLatest?: boolean;
}) {
  const [lenienceFilter, setLenienceFilter] = useState<LenienceFilter>("all");
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const rows = useMemo(() => run.rows ?? [], [run.rows]);

  const lenienceCounts = useMemo(
    () => ({
      human_more_lenient: rows.filter((r) => r.lenience === "human_more_lenient").length,
      agent_more_lenient: rows.filter((r) => r.lenience === "agent_more_lenient").length,
      equal: rows.filter((r) => r.lenience === "equal").length,
    }),
    [rows],
  );

  const filteredRows = useMemo(() => {
    if (lenienceFilter === "all") return rows;
    return rows.filter((r) => r.lenience === lenienceFilter);
  }, [rows, lenienceFilter]);

  const humanReview = (run.rollup.human_review_routing ??
    humanReviewRouting ??
    null) as HumanReviewRouting | null | undefined;
  const agentOnly = isAgentOnlyAdjudication(run);
  const adjudicatorLabel = adjudicatorGradeCallLabel(run.rollup.adjudicator_grade_call);
  const adjudicatorHelper = adjudicatorGradeCallHelper(run.rollup.adjudicator_grade_call);
  const exportStub = (run.header.client_name || "case").replace(/[^A-Za-z0-9_-]+/g, "_").slice(0, 60);

  const handleExport = async () => {
    setExporting(true);
    setExportError(null);
    try {
      await downloadAdjudicationWorksheetXlsx(
        caseId,
        `Adjudication_Worksheet_${exportStub}.xlsx`,
        run.id,
      );
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setExportError(detail || (e instanceof Error ? e.message : "Export failed"));
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <div className={CC_PANEL}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className={clsx(CC_SECTION_LABEL, "mb-1")}>
              {isLatest ? "Latest run" : "Previous run"} · {formatRunTimestamp(run.created_at)}
            </p>
            <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
              {agentOnly ? "Agent file assessment" : "File assessment comparison"}
            </p>
            <p className="text-sm text-slate-700 dark:text-slate-200 mt-1">
              {run.header.client_name || "Case"}
              {run.header.advice_summary ? ` · ${run.header.advice_summary}` : ""}
            </p>
            {run.header.human_sheet_reviewer ? (
              <p className="mt-1 text-xs text-slate-500">
                {run.header.human_benchmark_source === "guided_case_review"
                  ? "In-app review"
                  : "Human sheet"}
                : {run.header.human_sheet_reviewer}
                {run.header.human_sheet_date ? ` (${run.header.human_sheet_date})` : ""}
              </p>
            ) : null}
          </div>
          <Button
            plain
            type="button"
            onClick={() => void handleExport()}
            disabled={exporting}
            className={clsx(CC_GHOST_BTN, "shrink-0")}
            data-testid="adjudication-export-xlsx"
          >
            <FontAwesomeIcon icon={faFileArrowDown} className="h-3.5 w-3.5" />
            {exporting ? "Exporting…" : "Export worksheet"}
          </Button>
        </div>
        {exportError ? <p className={clsx("mt-2 text-xs", CC_ERROR_TEXT)}>{exportError}</p> : null}
        {agentOnly ? (
          <p className="mt-3 text-sm text-slate-400 leading-relaxed" data-testid="adjudication-agent-only-notice">
            No human file assessment sheet was submitted for this routine case. There are no
            human-vs-agent disagreements to review — the agent grade below stands as recorded.
          </p>
        ) : null}
      </div>

      <div className={CC_PANEL}>
        <p className={clsx(CC_SECTION_LABEL, "mb-3")}>Overall grade</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <GradeSummary label="Human reviewer" gradeRaw={run.rollup.human_overall_grade} />
          <GradeSummary label="Agent" gradeRaw={run.rollup.agent_final_grade} />
        </div>

        <div className="mt-4 grid gap-4 sm:grid-cols-2 border-t border-slate-200 dark:border-white/5 pt-4">
          <div>
            <p className="text-xs text-slate-500 mb-1.5">Formal compliance review</p>
            {humanReviewDisplayLabel(humanReview) ? (
              <>
                <HumanReviewStatusChip value={humanReview} size="panel" />
                {humanReviewHelperText(humanReview) ? (
                  <p className="mt-1.5 text-xs text-slate-500 leading-relaxed">
                    {humanReviewHelperText(humanReview)}
                  </p>
                ) : null}
              </>
            ) : (
              <span className="text-sm text-slate-500">—</span>
            )}
          </div>
          <div>
            <p className="text-xs text-slate-500 mb-1.5">Overall call</p>
            {adjudicatorLabel ? (
              <>
                <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{adjudicatorLabel}</p>
                {adjudicatorHelper ? (
                  <p className="mt-1 text-xs text-slate-500 leading-relaxed">{adjudicatorHelper}</p>
                ) : null}
              </>
            ) : (
              <span className="text-sm text-slate-500">—</span>
            )}
          </div>
        </div>

        {run.rollup.notes && !agentOnly ? (
          <p className="mt-4 border-t border-slate-200 dark:border-white/5 pt-4 text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
            {run.rollup.notes}
          </p>
        ) : null}
      </div>

      {agentOnly ? (
        <div className={CC_PANEL} data-testid="adjudication-no-disagreements">
          <p className="text-sm text-slate-500">
            Disagreement rows are only recorded when a human file assessment sheet is on file.
          </p>
        </div>
      ) : rows.length > 0 ? (
        <>
          <LenienceFilterBar
            value={lenienceFilter}
            onChange={setLenienceFilter}
            counts={lenienceCounts}
          />
          <DisagreementsSection rows={filteredRows} />
        </>
      ) : (
        <div className={CC_PANEL}>
          <p className="text-sm text-slate-500">No rule-level disagreements were recorded.</p>
        </div>
      )}
    </>
  );
}

export function AdjudicationTab({
  caseId,
  humanReviewRouting,
  hasReview = false,
}: {
  caseId: string;
  /** Case-level routing, shown when the adjudication rollup omits it. */
  humanReviewRouting?: HumanReviewRouting | null;
  /** True when the case has completed review grades. */
  hasReview?: boolean;
}) {
  const [run, setRun] = useState<AdjudicationRun | null>(null);
  const [task, setTask] = useState<AdjudicationTask | null>(null);
  const [readiness, setReadiness] = useState<CaseReviewAdjudicationReadiness | null>(null);
  const [checked, setChecked] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [triggering, setTriggering] = useState(false);
  const [triggerError, setTriggerError] = useState<string | null>(null);
  const [slow, setSlow] = useState(false);

  const outcome = adjudicationTaskOutcome(task, run);
  const running = outcome === "running";

  useEffect(() => {
    let cancelled = false;
    setRun(null);
    setTask(null);
    setReadiness(null);
    setChecked(false);
    setError(null);
    (async () => {
      try {
        const [runs, reviewReadiness, latestTask] = await Promise.all([
          listAdjudicationRuns(caseId, 1),
          getCaseReviewAdjudicationReadiness(caseId),
          getAdjudicationTask(caseId),
        ]);
        if (!cancelled) {
          setRun(runs[0] ?? null);
          setReadiness(reviewReadiness);
          setTask(latestTask);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Failed to load adjudication");
        }
      } finally {
        if (!cancelled) setChecked(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [caseId]);

  useEffect(() => {
    if (!running) return undefined;
    let cancelled = false;
    setSlow(false);

    const tick = async () => {
      try {
        const [runs, latestTask] = await Promise.all([listAdjudicationRuns(caseId, 1), getAdjudicationTask(caseId)]);
        if (cancelled) return;
        setRun(runs[0] ?? null);
        setTask(latestTask);
      } catch {
        // transient — retry on the next tick
      }
    };

    const interval = window.setInterval(() => void tick(), 3000);
    const slowTimer = window.setTimeout(() => {
      if (!cancelled) setSlow(true);
    }, 10 * 60_000);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.clearTimeout(slowTimer);
    };
  }, [running, caseId]);

  const handleTrigger = async () => {
    setTriggerError(null);
    setTriggering(true);
    try {
      await triggerAdjudicationFromCaseReview(caseId);
      const [latestTask, reviewReadiness] = await Promise.all([
        getAdjudicationTask(caseId),
        getCaseReviewAdjudicationReadiness(caseId),
      ]);
      setTask(latestTask);
      setReadiness(reviewReadiness);
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setTriggerError(detail || (e instanceof Error ? e.message : "Failed to start adjudication"));
    } finally {
      setTriggering(false);
    }
  };

  if (!checked) {
    return (
      <div className={clsx(CC_PANEL, "py-8 text-center text-sm text-slate-500")} data-testid="adjudication-loading">
        Loading adjudication…
      </div>
    );
  }

  if (error) {
    return (
      <div className={clsx(CC_PANEL, "text-sm", CC_ERROR_TEXT)} data-testid="adjudication-error">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="adjudication-panel">
      <CaseReviewAdjudicationSetupPanel
        readiness={readiness}
        running={running || triggering}
        triggerError={triggerError}
        onTrigger={() => void handleTrigger()}
      />

      {readiness?.adjudication_stale && readiness.can_trigger && run && !running ? (
        <div
          className={clsx(CC_ALERT_WARNING_PANEL, "px-4 py-3 text-sm")}
          data-testid="adjudication-stale-notice"
        >
          <p className={CC_ALERT_WARNING_TITLE}>Human review changed since this adjudication ran</p>
          <p className={clsx("mt-1", CC_ALERT_WARNING_BODY)}>
            Re-run adjudication to update it.
          </p>
        </div>
      ) : null}

      {running ? (
        <AdjudicationRunningPanel hadPreviousRun={Boolean(run)} queued={task?.status !== "working"} slow={slow} />
      ) : null}

      {outcome === "failed" || outcome === "no_result" ? (
        <div className={CC_PANEL} data-testid="adjudication-last-attempt">
          <p className="text-sm text-amber-500">
            {outcome === "failed"
              ? "The last adjudication run did not finish."
              : "The last adjudication run finished without saving results."}{" "}
            Run it again, or check Mission Control for details.
          </p>
          {task?.reason ? <p className="mt-1 text-xs text-slate-500">{task.reason}</p> : null}
        </div>
      ) : null}

      {run && !running ? (
        <AdjudicationRunPanel
          run={run}
          caseId={caseId}
          humanReviewRouting={humanReviewRouting}
        />
      ) : !running ? (
        <div className={CC_PANEL} data-testid="adjudication-empty">
          <p className="text-sm text-slate-500">
            No adjudication run yet. Finish case review, then run adjudication vs case review.
          </p>
        </div>
      ) : null}
    </div>
  );
}
